import React, { RefObject, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import ReactCalendar from 'react-calendar';
import Fit from 'react-fit';

import { Icon } from '@chargify/icons';

import { Input } from '../../atoms';
import { useOnClickOutside } from '../../../hooks';

import { Container, DatePickerStyles } from './DatePicker.styles';

import { CALENDAR_WIDTH } from './constants';
import { DatePickerTranslations } from './types';
import { defaultTranslations } from './translations';
import { KEYBOARD_KEYS } from '../../../constants';

export interface DatePickerProps {
  /**
   * Component value
   */
  value?: Date;
  /**
   * Component change event handler
   */
  onChange: (date: Date) => void;
  /**
   * Component translations
   */
  translations?: DatePickerTranslations;
  /**
   * Datepicker locale
   */
  locale?: string;
  /**
   * Minimum date that the user can select
   */
  minDate?: Date;
  /**
   * Maximum date that the user can select
   */
  maxDate?: Date;
  /**
   * Portal container used to mount options component
   */
  portalContainer?: RefObject<HTMLElement>;
  /**
   * Calendar component vertical aligment
   */
  calendarAlignment?: 'left' | 'right';
  /**
   * Modal container - can be used handle outside click
   */
  modalRef?: RefObject<HTMLElement>;
  /**
   * Datepicker disabled state
   */
  disabled?: boolean;
}

/**
- The Datepicker allows users to enter a date by choosing a date from the calendar.
**/
const DatePicker = ({
  value,
  translations = defaultTranslations,
  locale = 'en',
  minDate,
  maxDate,
  portalContainer,
  calendarAlignment = 'left',
  onChange,
  modalRef,
  disabled,
}: DatePickerProps) => {
  const [showCalendar, setCalendar] = useState(false);
  const calendarContainerRef = useRef(null);
  const inputRef = useRef(null);

  const { placeholder } = translations;
  const portalElement = portalContainer?.current;

  useOnClickOutside(
    [calendarContainerRef, portalContainer],
    () => {
      if (showCalendar) {
        setCalendar(false);
      }
    },
    modalRef?.current
  );
  const renderCalendar = () => (
    <ReactCalendar
      onChange={(date: Date) => {
        onChange(date);
        setCalendar(false);
      }}
      value={value}
      nextLabel={<Icon type="chevronRight" fill="black.main" size={12} />}
      next2Label={null}
      prevLabel={<Icon type="chevronLeft" fill="black.main" size={12} />}
      prev2Label={null}
      minDate={minDate}
      maxDate={maxDate}
      locale={locale}
    />
  );

  const calculateDropdownPosition = useCallback(() => {
    const { current: element } = calendarContainerRef;

    const { top, height, left, width } = element.getBoundingClientRect();

    const alignmentPosition =
      calendarAlignment === 'left' ? left : left - (CALENDAR_WIDTH - width);

    return {
      top: top + height + window.scrollY,
      left: alignmentPosition + window.scrollX,
      width: CALENDAR_WIDTH,
    };
  }, [calendarAlignment]);

  const handleBackspace = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === KEYBOARD_KEYS.BACKSPACE) {
      onChange(null);
    }
  };

  return (
    <Container ref={calendarContainerRef}>
      <DatePickerStyles />
      <div
        onClick={() => !disabled && setCalendar(!showCalendar)}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        data-testid="calendar-input"
      >
        <div ref={inputRef}>
          <Input
            onChange={() => false}
            inputSize="small"
            iconRight="calendar"
            placeholder={placeholder}
            readOnly
            onKeyDown={handleBackspace}
            value={value?.toLocaleDateString(locale) || ''}
            disabled={disabled}
          />
        </div>
      </div>
      {showCalendar && (
        <>
          {portalElement ? (
            <>
              {createPortal(
                <div
                  style={{
                    position: 'absolute',
                    zIndex: 1,
                    ...calculateDropdownPosition(),
                  }}
                >
                  {renderCalendar()}
                </div>,
                portalElement
              )}
            </>
          ) : (
            <Fit invertSecondaryAxis={calendarAlignment === 'right'}>
              <div
                style={{ position: 'absolute', width: CALENDAR_WIDTH }}
                data-testid="calendar-container"
              >
                {renderCalendar()}
              </div>
            </Fit>
          )}
        </>
      )}
    </Container>
  );
};

export default DatePicker;
